// UK site competencies, plus helpers that turn the API's qualification tags
// into editable records and back.
//
// Storage: each qualification is a company tag on the member
// (contractor_staff_tag_assignments) with its expiry, level, card number,
// issuer, issue date and evidence file id stored as real columns. Older
// records kept the details in `staffInfo.qualDetails[name]` (JSON on the
// user); they're still read as a fallback and moved onto the tag on next save
// (see stripLegacyQualDetails).

import { todayLocalISO, formatDate } from './dates';
import { TONES } from '../shared/StatusChip';

/** Catalogue of common UK competencies. `years` = typical validity, used as a hint only. */
export const QUAL_CATALOGUE = [
  { group: 'Site cards', items: [
    { name: 'CSCS card — Labourer (green)', years: 5 },
    { name: 'CSCS card — Skilled worker (blue)', years: 5 },
    { name: 'CSCS card — Advanced craft (gold)', years: 5 },
    { name: 'CSCS card — Supervisor (gold)', years: 5 },
    { name: 'CSCS card — Manager (black)', years: 5 },
    { name: 'CSCS card — Trainee (red)', years: 5 },
    { name: 'CSCS card — Apprentice', years: 4 },
    { name: 'CSCS card — Visitor', years: 1 },
  ] },
  { group: 'Supervision & management', items: [
    { name: 'SSSTS (Site Supervisor Safety Training Scheme)', years: 5 },
    { name: 'SMSTS (Site Management Safety Training Scheme)', years: 5 },
    { name: 'Fire warden', years: 3 },
    { name: 'First Aid at Work (FAW)', years: 3 },
    { name: 'Emergency First Aid at Work (EFAW)', years: 3 },
  ] },
  { group: 'Access & height', items: [
    { name: 'IPAF 3a (mobile vertical)', years: 5 },
    { name: 'IPAF 3b (static boom)', years: 5 },
    { name: 'IPAF 3a/3b', years: 5 },
    { name: 'PASMA (mobile access towers)', years: 5 },
    { name: 'Working at height', years: 3 },
    { name: 'Confined spaces', years: 3 },
  ] },
  { group: 'Gas, electrical & fire systems', items: [
    { name: 'Gas Safe registered', years: 1 },
    { name: '18th Edition (BS 7671) wiring regulations', years: null },
    { name: 'NICEIC registered', years: 1 },
    { name: 'NAPIT registered', years: 1 },
    { name: 'PAT testing (in-service inspection and testing)', years: null },
    { name: 'Fire alarm (FIA training)', years: 5 },
    { name: 'BAFE registered (fire safety)', years: 1 },
    { name: 'Emergency lighting (BS 5266)', years: null },
  ] },
  { group: 'Lifting, water & hazards', items: [
    { name: 'LOLER competent person', years: null },
    { name: 'Legionella (L8) awareness', years: 3 },
    { name: 'Legionella (L8) responsible person', years: 3 },
    { name: 'Asbestos awareness (Category A)', years: 1 },
    { name: 'Manual handling', years: 3 },
  ] },
];

export const CATALOGUE_NAMES = QUAL_CATALOGUE.flatMap((g) => g.items.map((i) => i.name));
const catalogueOrder = new Map(CATALOGUE_NAMES.map((n, i) => [n.toLowerCase(), i]));

/** Sort qualification names: catalogue order first, then custom ones A–Z. */
export const sortQualNames = (names) => [...names].sort((a, b) => {
  const ia = catalogueOrder.has(a.toLowerCase()) ? catalogueOrder.get(a.toLowerCase()) : 1e6;
  const ib = catalogueOrder.has(b.toLowerCase()) ? catalogueOrder.get(b.toLowerCase()) : 1e6;
  return ia - ib || a.localeCompare(b);
});

/** Is this qualification a site card (CSCS etc.)? Used for the ID badge. */
export const isSiteCard = (name) => /cscs|ecs card|cpcs|npors/i.test(String(name || ''));

const DAY = 86400000;
const daysUntil = (iso) => {
  const t = todayLocalISO();
  const [ty, tm, td] = t.split('-').map(Number);
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(ty, tm - 1, td)) / DAY);
};

/**
 * Expiry state for one qualification. Always text + icon, never colour alone.
 *   none    — neutral "No expiry recorded"
 *   expired — red "Expired"
 *   soon    — amber "Expires in 12 days"
 *   valid   — green "Valid until 01 Oct 2027"
 */
export function qualExpiryState(expiry, { soonDays = 30 } = {}) {
  if (!expiry || !/^\d{4}-\d{2}-\d{2}/.test(String(expiry))) {
    return { key: 'none', tone: 'neutral', icon: 'fa-regular fa-circle', label: 'No expiry recorded', short: 'No expiry', days: null };
  }
  const days = daysUntil(expiry);
  if (days < 0) {
    return { key: 'expired', tone: 'danger', icon: 'fa-solid fa-circle-xmark', label: `Expired ${formatDate(String(expiry).slice(0, 10))}`, short: 'Expired', days };
  }
  if (days <= soonDays) {
    const label = days === 0 ? 'Expires today' : `Expires in ${days} ${days === 1 ? 'day' : 'days'}`;
    return { key: 'soon', tone: 'warning', icon: 'fa-solid fa-clock', label, short: label, days };
  }
  return { key: 'valid', tone: 'success', icon: 'fa-solid fa-circle-check', label: `Valid until ${formatDate(String(expiry).slice(0, 10))}`, short: 'Valid', days };
}

export const toneColours = (tone) => TONES[tone] || TONES.neutral;

const EMPTY_DETAIL = { cardNumber: '', issuer: '', issueDate: '', evidenceFileId: null, evidenceFileName: '' };

/** API tags + staff info → editable records. */
export function qualsFromRecord(tags, info) {
  const details = (info && typeof info.qualDetails === 'object' && info.qualDetails) || {};
  return (Array.isArray(tags) ? tags : [])
    .map((t) => (typeof t === 'string' ? { name: t } : t))
    .filter((t) => t && (t.name || t.tag_name))
    .map((t) => {
      const name = t.name || t.tag_name;
      const legacy = details[name] || {};
      return {
        id: t.id,
        name,
        expiry: (t.expires_at || t.expiry || '').slice(0, 10),
        level: t.level || '',
        ...EMPTY_DETAIL,
        // Server columns win; legacy qualDetails fill any gaps until the next save migrates them.
        cardNumber: t.card_number || legacy.cardNumber || '',
        issuer: t.issuer || legacy.issuer || '',
        issueDate: (t.issued_at || legacy.issueDate || '').slice(0, 10),
        evidenceFileId: t.evidence_file_id || legacy.evidenceFileId || null,
        evidenceFileName: t.evidence_file_name || legacy.evidenceFileName || '',
      };
    });
}

/** Editable records → { tags } for PATCH /staff/:id or /staff/me (details go on each tag). */
export function qualsToPayload(quals) {
  const tags = quals.map((q) => ({
    name: q.name,
    expires_at: q.expiry || null,
    level: q.level || null,
    card_number: (q.cardNumber || '').trim() || null,
    issuer: (q.issuer || '').trim() || null,
    issued_at: q.issueDate || null,
    evidence_file_id: q.evidenceFileId || null,
  }));
  return { tags };
}

/** Staff info without the legacy qualDetails blob — send this alongside `tags` so old details migrate. */
export function stripLegacyQualDetails(info) {
  const next = { ...(info || {}) };
  delete next.qualDetails;
  return next;
}

export const newQual = (name) => ({ name, expiry: '', level: '', ...EMPTY_DETAIL });

/** Field errors for one record, keyed by field. */
export function qualErrors(q) {
  const e = {};
  const today = todayLocalISO();
  if (q.issueDate && q.issueDate > today) e.issueDate = "Issue date can't be in the future";
  if (q.issueDate && q.expiry && q.expiry < q.issueDate) e.expiry = 'Expiry must be after the issue date';
  return e;
}

/** Counts for list chips. */
export function qualCounts(quals) {
  let expired = 0; let soon = 0;
  quals.forEach((q) => {
    const k = qualExpiryState(q.expiry).key;
    if (k === 'expired') expired += 1;
    else if (k === 'soon') soon += 1;
  });
  return { expired, soon };
}
